/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, TextField, Icon, ErrorText } from '@components/atoms'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import * as locale from 'locale-codes'
import { useAppSelector } from '@redux/index'
import { I18nType } from '@schemas'

interface PropsType {
	title?: string
	className?: string
	i18n: I18nType[]
	tableName: string
	tableId: string
	menuId: string
	value: string
	disabled?: boolean
	onSave?: (newI18n: I18nType) => void
}

/** 다국어 표 — 언어별 헤어라인 행(라벨 위 · 값 아래). 편집은 연필, 저장은 체크. 카드·라운드 없음 */
export const I18nEditor = (props: PropsType) => {
	// redux state
	const i18nLanguageList: string[] = useAppSelector(
		state => state.i18n.supportedLanguageList,
	)

	const emptyI18n: I18nType = {
		id: '',
		tableName: '',
		tableId: '',
		menuId: '',
		isoLanguageCode: '',
		value: '',
		title: '',
	}

	// state
	const [editI18n, setEditI18n] = React.useState<I18nType>(emptyI18n)
	const [showError, setShowError] = React.useState<boolean>(false)

	// varialbes
	const disabled: boolean = props.disabled ?? false

	const onEditClick = React.useCallback((anI18n: I18nType) => {
		setEditI18n(anI18n)
		setShowError(false)
	}, [])

	const onTextChange = (e: any) => {
		const newTitle = e.target.value

		const newEditI18n: I18nType = { ...editI18n, title: newTitle }
		setEditI18n(newEditI18n)
	}

	const onInputEnterSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			onSaveClick()
		}
	}

	const onSaveClick = React.useCallback(() => {
		if (editI18n.title === '') {
			setShowError(true)
		} else {
			const newI18n: I18nType = {
				id: editI18n.id,
				tableName: props.tableName,
				tableId: props.tableId,
				menuId: props.menuId,
				isoLanguageCode: editI18n.isoLanguageCode,
				value: '',
				title: editI18n.title,
			}

			setEditI18n(emptyI18n)

			if (props.onSave) {
				props.onSave(newI18n)
			}
		}
	}, [editI18n])

	return (
		<View css={style} className={props.className ?? ''}>
			<View className="i18nView">
				{props.title && <span className="title">{props.title}</span>}
				<View className="i18nListView">
					{i18nLanguageList.map(anLanguageCode => {
						const localeData: locale.ILocale = locale.getByTag(anLanguageCode)
						const thisI18n = props.i18n.filter(
							anI18n =>
								anI18n.isoLanguageCode.toLowerCase() === anLanguageCode.toLowerCase(),
						)

						const code = localeData.tag
						const languageName: string =
							code === 'zh-CN'
								? '中文'
								: localeData.local
								? localeData.local
								: localeData.name
								? localeData.name
								: ''
						const title = thisI18n.length > 0 ? thisI18n[0].title : '-'
						const isEditMode: boolean = editI18n.isoLanguageCode === code

						return (
							<View key={anLanguageCode} className="i18nItemOuter">
								<View
									className={`i18nItemView ${disabled ? 'disabled' : ''} ${
										isEditMode ? 'editing' : ''
									}`}
								>
									<View className="i18nItemTextView">
										<span className="i18nItemLabel">{`${languageName} (${code})`}</span>
										{isEditMode ? (
											<>
												<TextField
													className="newI18nTextField"
													variant="standard"
													size="small"
													autoFocus={true}
													defaultValue={title === '-' ? '' : title}
													onChange={onTextChange}
													error={showError}
													onKeyUp={onInputEnterSubmit}
												/>
												{showError && (
													<ErrorText category="c2" css={{ marginBottom: 0 }}>
														빈 텍스트는 등록할 수 없습니다.
													</ErrorText>
												)}
											</>
										) : (
											<span className={`i18nTitle ${title === '-' ? 'empty' : ''}`}>
												{title}
											</span>
										)}
									</View>

									{isEditMode ? (
										<Pressable
											className="i18nItemRightButton save"
											onClick={onSaveClick}
											disabled={disabled}
											aria-label="저장"
										>
											<Icon name="check" className="i18nItemRightIcon" />
										</Pressable>
									) : (
										<>
											{!disabled && (
												<Pressable
													className="i18nItemRightButton"
													aria-label="수정"
													onClick={() =>
														onEditClick(
															thisI18n.length > 0
																? {
																		...thisI18n[0],
																		isoLanguageCode: code,
																		title: title,
																  }
																: {
																		id: 'new',
																		tableName: props.tableName,
																		tableId: props.tableId,
																		menuId: props.menuId,
																		isoLanguageCode: code,
																		value: '',
																		title: '',
																  },
														)
													}
													disabled={disabled}
												>
													<Icon name="pencil-outline" className="i18nItemRightIcon" />
												</Pressable>
											)}
										</>
									)}
								</View>
							</View>
						)
					})}
				</View>
			</View>
		</View>
	)
}

const style = css({
	width: '100%',
	height: '100%',
	fontFamily: boFont.ui,

	'.i18nView': {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		overflow: 'hidden',

		'.title': {
			flexShrink: 0,
			display: 'block',
			paddingBottom: 10,
			fontSize: 11,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
		},

		'.i18nListView': {
			flex: 1,
			minHeight: 0,
			overflow: 'auto',
			borderTop: `1px solid ${mono.line}`,

			'.i18nItemOuter': {
				'.i18nItemView': {
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 12,
					width: '100%',
					minHeight: 56,
					padding: '10px 0',
					borderBottom: `1px solid ${mono.line}`,
					textAlign: 'left',

					'&.disabled': {
						opacity: 0.5,
					},

					'.i18nItemTextView': {
						display: 'flex',
						flexDirection: 'column',
						gap: 4,
						minWidth: 0,

						'.i18nItemLabel': {
							fontSize: 11,
							letterSpacing: '.08em',
							color: mono.faint,
						},
						'.i18nTitle': {
							fontSize: 14,
							fontWeight: 300,
							color: mono.ink,
							'&.empty': { color: mono.faint },
						},

						'.newI18nTextField': {
							width: '11rem',
						},
					},

					'.i18nItemRightButton': {
						flexShrink: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 28,
						height: 28,
						padding: 0,
						borderRadius: 2,
						transition: 'color 200ms ease-out, background-color 200ms ease-out',

						'.i18nItemRightIcon': {
							color: mono.faint,
							fontSize: 16,
							transition: 'color 200ms ease-out',
						},

						'&:hover:not(:disabled) .i18nItemRightIcon': { color: mono.ink },

						'&.save': {
							background: mono.ink,
							'.i18nItemRightIcon': { color: '#FFFFFF' },
							'&:hover:not(:disabled)': { background: mono.ctaBg },
						},
					},
				},
			},
		},
	},
})
